import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getNode } from '../../api/client';
import { buildNodeConnections } from '../../lib/nodeConnections';
import { findOutgoingOverlapGroups } from '../../lib/routeGeometry';
import { NODE_TYPE_LABELS, resolveNodeType } from '../../lib/nodeConfig';
import { normalizeLegacyNodeDraft } from '../../lib/normalizeLegacyNode';
import { useEditorStore } from '../../store/editorStore';
import type { ConfigurableNodeFields, NodeConnections } from '../../types/nodeConfig';
import type { NodeType, ProjectDocument, ProjectNode } from '../../types/project';
import type { ValidationReport } from '../../types/validation';
import { hasBlockingErrors } from '../../types/validation';
import BasicNodeFields from './BasicNodeFields';
import ConnectionInfoPanel from './ConnectionInfoPanel';
import GateNodeConfig from './GateNodeConfig';
import InputNodeConfig from './InputNodeConfig';
import MergerNodeConfig from './MergerNodeConfig';
import NodeDialogShell from './NodeDialogShell';
import NodeValidationPanel from './NodeValidationPanel';
import OutputNodeConfig from './OutputNodeConfig';
import ProcessorNodeConfig from './ProcessorNodeConfig';
import SplitterNodeConfig from './SplitterNodeConfig';
import WaypointNodeConfig from './WaypointNodeConfig';

function collectProductTypes(document: ProjectDocument | null) {
  if (!document) return [];
  const byId = new Map<string, { id: string; name: string }>();
  for (const node of document.nodes) {
    if (resolveNodeType(node) !== 'input') continue;
    const products =
      (node.product_types as Array<{ id: string; name: string }> | undefined) ??
      [];
    for (const p of products) {
      if (!p.id || byId.has(p.id)) continue;
      byId.set(p.id, { id: p.id, name: p.name });
    }
  }
  return Array.from(byId.values());
}

export default function NodeEditDialog() {
  const projectId = useEditorStore((s) => s.projectId);
  const document = useEditorStore((s) => s.document);
  const editingNodeId = useEditorStore((s) => s.editingNodeId);
  const readOnly = useEditorStore((s) => s.permission) !== 'edit';
  const closeNodeEdit = useEditorStore((s) => s.closeNodeEdit);
  const saveProject = useEditorStore((s) => s.saveProject);
  const removeNodeFromDocument = useEditorStore((s) => s.removeNodeFromDocument);
  const updateNodeInStore = useEditorStore((s) => s.updateNode);

  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [connections, setConnections] = useState<NodeConnections>({
    incoming: [],
    outgoing: [],
  });
  const [validation, setValidation] = useState<ValidationReport | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasUserEditedRef = useRef(false);

  const productTypeOptions = useMemo(
    () => collectProductTypes(document),
    [document],
  );

  const overlapGroups = useMemo(() => {
    if (!document || !editingNodeId) return [];
    return findOutgoingOverlapGroups(document.routes, editingNodeId);
  }, [document, editingNodeId]);

  useEffect(() => {
    const doc = useEditorStore.getState().document;
    if (!editingNodeId || !doc) {
      setDraft(null);
      hasUserEditedRef.current = false;
      return;
    }

    const local = doc.nodes.find((n) => n.id === editingNodeId);
    if (!local) {
      setDraft(null);
      return;
    }

    hasUserEditedRef.current = false;
    setDraft(normalizeLegacyNodeDraft({ ...local }));
    setConnections(buildNodeConnections(doc, editingNodeId));
    setValidation(null);
    setError(null);
  }, [editingNodeId]);

  useEffect(() => {
    if (!editingNodeId || !document) return;
    setConnections(buildNodeConnections(document, editingNodeId));
  }, [editingNodeId, document]);

  useEffect(() => {
    if (!editingNodeId || !projectId) return;

    let cancelled = false;
    void getNode(projectId, editingNodeId)
      .then((res) => {
        if (cancelled || hasUserEditedRef.current) return;
        setDraft(normalizeLegacyNodeDraft(res.node as Record<string, unknown>));
        setConnections(res.connections);
        setValidation(res.validation);
      })
      .catch(() => {
        /* use local draft if API unavailable */
      });

    return () => {
      cancelled = true;
    };
  }, [editingNodeId, projectId]);

  const patchDraft = useCallback(
    (patch: Record<string, unknown>) => {
      hasUserEditedRef.current = true;
      setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
      setValidation(null);
      if (editingNodeId) {
        updateNodeInStore(editingNodeId, patch as Partial<ProjectNode>);
      }
    },
    [editingNodeId, updateNodeInStore],
  );

  const handleSave = async () => {
    if (!projectId || !draft || readOnly) return;
    const nodeId = String(draft.id);
    setSaving(true);
    setError(null);
    try {
      const saved = await saveProject();
      if (!saved) {
        setError(useEditorStore.getState().error ?? 'Failed to save node');
        return;
      }
      const res = await getNode(projectId, nodeId);
      hasUserEditedRef.current = false;
      setDraft(normalizeLegacyNodeDraft(res.node as Record<string, unknown>));
      setConnections(res.connections);
      setValidation(res.validation);
      if (!hasBlockingErrors(res.validation)) {
        closeNodeEdit();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save node');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!draft || readOnly) return;
    const confirmed = window.confirm(
      `Delete node "${String(draft.name)}"? Connected routes will be removed.`,
    );
    if (!confirmed) return;
    removeNodeFromDocument(String(draft.id));
    closeNodeEdit();
  };

  if (!editingNodeId || !draft) return null;

  const nodeType = (draft.type ?? draft.node_type ?? 'waypoint') as NodeType;
  const basicNode = draft as unknown as ConfigurableNodeFields;

  return (
    <NodeDialogShell
      open
      title={String(draft.name ?? 'Node')}
      typeLabel={NODE_TYPE_LABELS[nodeType] ?? nodeType}
      validation={validation}
      wide={nodeType === 'processor'}
      onClose={closeNodeEdit}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={closeNodeEdit}>
            Cancel
          </button>
          {!readOnly && (
            <button
              type="button"
              className="btn-danger"
              disabled={saving}
              onClick={() => void handleDelete()}
            >
              Delete
            </button>
          )}
          {!readOnly && (
            <button
              type="button"
              className="btn-primary"
              disabled={saving}
              onClick={() => void handleSave()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="alert-danger px-3 py-2 text-sm">
            {error}
          </p>
        )}

        <BasicNodeFields
          node={{ ...basicNode, type: nodeType }}
          readOnly={readOnly}
          onChange={(patch) => patchDraft(patch as Record<string, unknown>)}
        />

        {nodeType === 'input' && (
          <InputNodeConfig
            node={draft}
            readOnly={readOnly}
            onChange={patchDraft}
          />
        )}
        {(nodeType === 'splitter' || nodeType === 'diverger') && (
          <SplitterNodeConfig
            node={draft}
            connections={connections}
            productTypeOptions={productTypeOptions}
            readOnly={readOnly}
            onChange={patchDraft}
          />
        )}
        {nodeType === 'gate' && (
          <GateNodeConfig
            node={draft}
            connections={connections}
            readOnly={readOnly}
            onChange={patchDraft}
          />
        )}
        {nodeType === 'merger' && (
          <MergerNodeConfig
            node={draft}
            connections={connections}
            readOnly={readOnly}
            onChange={patchDraft}
          />
        )}
        {nodeType === 'output' && (
          <OutputNodeConfig
            node={draft}
            productTypeOptions={productTypeOptions}
            readOnly={readOnly}
            onChange={patchDraft}
          />
        )}
        {nodeType === 'processor' && (
          <ProcessorNodeConfig
            node={draft}
            connections={connections}
            productTypeOptions={productTypeOptions}
            serverValidation={validation}
            readOnly={readOnly}
            onChange={patchDraft}
          />
        )}
        {nodeType === 'waypoint' && (
          <WaypointNodeConfig
            node={draft}
            readOnly={readOnly}
            onChange={patchDraft}
          />
        )}

        <ConnectionInfoPanel
          connections={connections}
          overlapGroups={overlapGroups}
        />
        {nodeType !== 'processor' && (
          <NodeValidationPanel validation={validation} />
        )}
      </div>
    </NodeDialogShell>
  );
}
